
import { useState, useEffect, useRef } from 'react';
import { Assessment } from '@/types/assessment';
import { ResponseData, UseAssessmentResponsesProps, UseAssessmentResponsesResult } from './assessment/types';
import { useLocalStorageResponses } from './assessment/useLocalStorage';
import { useAssessmentSubmission } from './assessment/useAssessmentSubmission';
import { calculateScoresByCategory, canMoveForwardFromQuestion, validateAllRequiredQuestionsAnswered } from './assessment/responseUtils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export type { ResponseData } from './assessment/types';

export function useAssessmentResponses({ 
  assessment, 
  categories 
}: { 
  assessment: Assessment, 
  categories: any[] 
}): any {
  const { responses, setResponses, clearStoredResponses } = useLocalStorageResponses(assessment.id);
  const [calculatedScores, setCalculatedScores] = useState<Record<string, {current: number, target: number}>>({});
  const [submittedResponses, setSubmittedResponses] = useState<Record<string, ResponseData>>({});
  const { submitAssessment: submitAssessmentToAPI, isSubmitting } = useAssessmentSubmission(assessment, categories, responses);
  const { toast } = useToast();
  const responseCountRef = useRef(0);
  
  // Initialize calculated scores only once when the component mounts or when response count changes
  useEffect(() => {
    const responseCount = Object.keys(responses).length;

    // Only recalculate if response count changes or on initial mount
    if (responseCount !== responseCountRef.current) {
      responseCountRef.current = responseCount;
      
      // Only calculate if we have responses
      if (responseCount > 0) {
        const scores = calculateScoresByCategory(
          responses, 
          categories, 
          assessment.assessmentType || 'statusObjective'
        );
        setCalculatedScores(scores);
      }
    }
  }, [responses, categories, assessment.assessmentType]);
  
  // Handle response changes
  const handleResponseChange = (questionId: string, current: string[] | number, target: string[] | number) => {
    setResponses((prev: any) => {
      const updatedResponses = {
        ...prev,
        [questionId]: { questionId, current, target }
      };
      
      return updatedResponses;
    });
  };

  // Batch set multiple responses at once (avoids stale closure issues)
  const batchSetResponses = (newResponses: Record<string, { questionId: string; current: string[] | number; target: string[] | number }>) => {
    setResponses((prev: any) => ({
      ...prev,
      ...newResponses,
    }));
  };
  
  // Calculate scores using the utility function - only called explicitly
  const calculateScores = () => {
    const scoresByCategory = calculateScoresByCategory(
      responses, 
      categories, 
      assessment.assessmentType || 'statusObjective'
    );
    
    console.log('[useAssessmentResponses] Calculated scores:', {
      responses: Object.entries(responses).map(([id, r]) => ({ 
        questionId: id, 
        current: r.current, 
        target: r.target 
      })),
      scoresByCategory,
      categories: categories.map(c => ({ id: c.id, name: c.name }))
    });
    
    setCalculatedScores(scoresByCategory);
    return scoresByCategory;
  };
  
  // Submit assessment response with validation
  const submitAssessment = async (personalInfo: {
    fullName: string;
    email: string;
    company: string;
    designation: string;
    consentGiven: boolean;
  }): Promise<boolean> => {
    try {
      // Validate all required questions are answered
      const validation = validateAllRequiredQuestionsAnswered(responses, categories);
      
      if (!validation.valid) {
        // Show error toast with unanswered questions
        toast({
          variant: "destructive",
          title: "Incomplete Assessment",
          description: `Please answer all required questions before submitting. (${validation.unansweredQuestions.length} questions remaining)`
        });
        
        return false;
      }
      
      // Calculate scores one time before submission
      const scoresByCategory = calculateScores();
      console.log('[useAssessmentResponses] Submit assessment with scores:', scoresByCategory);
      
      // Save the scores before submission to ensure they're available for results page
      localStorage.setItem('assessment-results-scores', JSON.stringify(scoresByCategory));
      
      // Store responses before clearing them for recommendations
      setSubmittedResponses(responses);
      
      const success = await submitAssessmentToAPI(personalInfo);
      
      if (success) {
        // Only clear responses after successful submission to prevent data loss
        clearStoredResponses();
        
        console.log('[useAssessmentResponses] ✅ Submission successful. Email with styled PDF will be sent after results page renders');
        
        return true;
      }
      
      // Show error toast if API submission failed without throwing
      toast({
        variant: "destructive",
        title: "Submission Failed",
        description: "We couldn't save your assessment. Please try again in a moment."
      });
      return false;
    } catch (error) {
      console.error('[useAssessmentResponses] Error submitting assessment:', error);
      toast({
        variant: "destructive",
        title: "Submission Failed",
        description: "There was an error submitting your assessment. Please try again."
      });
      return false;
    }
  };
  
  // Check if we can move forward (now always returns true for navigation)
  const canMoveForward = (currentQuestion?: any) => {
    return canMoveForwardFromQuestion(currentQuestion, responses);
  };
  
  // Clear all responses (both localStorage and in-memory state)
  const clearAllResponses = () => {
    clearStoredResponses();
    setSubmittedResponses({});
    setCalculatedScores({});
    responseCountRef.current = 0;
  };
  
  return {
    responses: Object.keys(submittedResponses).length > 0 ? submittedResponses : responses,
    calculatedScores,
    responseCount: Object.keys(responses).length,
    handleResponseChange,
    batchSetResponses,
    canMoveForward,
    calculateScores,
    submitAssessment,
    isSubmitting,
    clearAllResponses
  };
}
